import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';

import { AwsEnvironment } from '../aws/environment';
import { AwsSdk } from '../aws/sdk';
import { MQTTService } from '../mqtt/mqtt.service';
import { Message } from '../publisher/message';
import { PublisherService } from '../publisher/publisher.service';
import { UserPreferencesService } from './preferences.service';
import { Preferences, User } from './user';

enum UserAuthenticationMessageType {
    LOGIN, LOGOUT
}

export class UserAuthenticationMessage extends Message {

    public static readonly Type = UserAuthenticationMessageType

    constructor(
        public user: User,
        public type: UserAuthenticationMessageType
    ) {
        super(user);
    }
}

export const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000;

@Injectable()
export class UserService {

    static inactivityTimer: any = null;

    private renewCredentialsTimer: any;
    private sessionStorage: Storage;
    private userAuthenticationChannel: Subject<Message>;

    currentUser: User;

    constructor(private awsEnv: AwsEnvironment, private awsSdk: AwsSdk, private preferencesService: UserPreferencesService,
        private mqtt: MQTTService, private router: Router, private publisher: PublisherService) {
        this.sessionStorage = window.sessionStorage;
        this.userAuthenticationChannel = this.publisher.createChannel('user-authentication');
    }

    logOut() {
        this.currentUser = null;
        this.userAuthenticationChannel.next(new UserAuthenticationMessage(null, UserAuthenticationMessage.Type.LOGOUT));
        if (this.renewCredentialsTimer) {
            clearTimeout(this.renewCredentialsTimer);
            this.renewCredentialsTimer = null;
        }

        this.disableLogoutForInactivity();
        this.clearCredentials();
        this.router.navigate(['/login']);
    }

    getCurrentUser(): User {
        return this.currentUser;
    }

    updatePreferencesOnCurrentUser(): Observable<Preferences> {
        return this.preferencesService.update(this.getCurrentUser());
    }

    setCurrentUser(user: User, isTrial?: boolean): Promise<void> {
        return this.awsSdk.getTempCredentials(user.Email, user.RoleArn, user.Cognito.Token)
            .then((data: any) => {
                this.currentUser = user;
                this.setAwsCredentials(data);
                this.updatePreferencesOnCurrentUser().subscribe(result => { });
                this.sessionStorage.setItem('currentUser', JSON.stringify(this.currentUser));
                this.connectToMQTT();
                this.enableLogoutForInactivity();
                this.userAuthenticationChannel.next(new UserAuthenticationMessage(user, UserAuthenticationMessage.Type.LOGIN));
            })
            .catch((error: any) => {
                console.error('Cannot get aws credentials ' + error);
                throw error;
            });
    }

    connectToMQTT(): void {
        // this.mqtt.connect(this.crtUser).subscribe(
        //     (connected: boolean) => { },
        //     (error: string) => {
        //         //try to reconnect on error
        //         setTimeout(() => {
        //             if (this.isLoggedIn()) {
        //                 this.connectToMQTT();
        //             }
        //         }, 1000);
        //     },
        //     () => {
        //     });
    }

    isLoggedIn(): boolean {
        return this.currentUser != null;
    }

    getApiUrl(): string {
        let baseUrl = this.currentUser.ApiServiceUrl;
        return `${baseUrl}/${this.currentUser.Cognito.IdentityId}`;
    }

    updateFromStorage(): boolean {
        let storageCrtUser = JSON.parse(this.sessionStorage.getItem('currentUser'));
        let storageCredentials = JSON.parse(this.sessionStorage.getItem('credentials'));

        if (storageCrtUser && storageCredentials) {
            this.currentUser = storageCrtUser;

            this.awsEnv.setCredentials(storageCredentials.accessKeyId, storageCredentials.secretAccessKey,
                storageCredentials.sessionToken, new Date(storageCredentials.expireDate));
            this.scheduleToRenewAwsCredentials();

            /* TODO: maybe use the same setCurrentUser? Unify storage and normal login */
            this.connectToMQTT();
            this.enableLogoutForInactivity();
            return true;
        }
        return false;
    }

    private setAwsCredentials(responseData: any) {
        this.awsEnv.setCredentials(responseData.Credentials.AccessKeyId, responseData.Credentials.SecretAccessKey, responseData.Credentials.SessionToken,
            responseData.Credentials.Expiration);
        this.sessionStorage.setItem('credentials', JSON.stringify(this.awsEnv.getCredentials()));
        this.scheduleToRenewAwsCredentials();
    }

    private scheduleToRenewAwsCredentials() {
        if (!this.awsEnv.getCredentials() || !this.currentUser) {
            return;
        }

        let timeLeft = this.awsEnv.getCredentials().expireDate.getTime() - new Date().getTime();
        if (timeLeft < 0) {
            console.error('[UserService] - credentilas expired, too late to renew, logging out');
            this.logOut();
        }
        else if (timeLeft < 30 * 60 * 1000) { // under 30 minutes 
            console.log('[UserService] - Starting AWS credentials renewal' + new Date());
            return this.awsSdk.renewCredentials(this.currentUser.Email, this.currentUser.RoleArn, this.awsEnv.getCredentials())
                .then(data => {
                    this.setAwsCredentials(data);
                    console.info('[UserService] - AWS credentials were renewed' + new Date());
                })
                .catch(error => {
                    console.error('[UserService] - Cannot renew aws credentials ' + error);
                    this.logOut();
                });
        } else {
            this.renewCredentialsTimer = setTimeout(() => { this.scheduleToRenewAwsCredentials(); }, 60 * 1000);
        }
    }

    private clearCredentials(): void {
        // at this moment only credentials we keep on window
        this.sessionStorage.clear();
        this.awsEnv.clearCredentials();
    }

    private enableLogoutForInactivity() {
        UserService.resetInactivityTimer(this);

        // DOM Events
        document.onmousedown = (() => UserService.resetInactivityTimer(this));
        document.onkeypress = (() => UserService.resetInactivityTimer(this));
    }

    private disableLogoutForInactivity() {

        if (UserService.inactivityTimer) {
            clearTimeout(UserService.inactivityTimer);
            UserService.inactivityTimer = null;
        }

        // DOM Events
        document.onmousedown = null;
        document.onkeypress = null;
    }

    private static resetInactivityTimer(userService: UserService) {
        if (UserService.inactivityTimer) {
            clearTimeout(UserService.inactivityTimer);
            UserService.inactivityTimer = null;
        }
        UserService.inactivityTimer = setTimeout(() => {
            console.info('[UserService] - Logging out due to inactivity' + new Date());
            userService.logOut();
        }, INACTIVITY_TIMEOUT);
    }

}


// WEBPACK FOOTER //
// ./src/app/user/user.service.ts